import { SelectedTranslatedTrack } from "providers/AudioProvider/types";

interface checkSelectedTracksProps {
    selectedTranslatedTrackIds: SelectedTranslatedTrack[];
    ind: number;
}
export const checkSelectedTracks = ({
    selectedTranslatedTrackIds,
    ind,
}: checkSelectedTracksProps) => {
    let modalText = "";
    if (selectedTranslatedTrackIds.length) {
        if (!selectedTranslatedTrackIds.find((item) => item.ind === ind)) {
            modalText = "for group movement you need to select this track";
            return modalText;
        }
        let prevInd = selectedTranslatedTrackIds[0].ind;
        for (let i = 1; i < selectedTranslatedTrackIds.length; i++) {
            if (selectedTranslatedTrackIds[i].ind - prevInd > 1) {
                modalText = "tracks must be selected sequentially";
                break;
            } else {
                prevInd = selectedTranslatedTrackIds[i].ind;
            }
        }
    }
    return modalText;
};

/**
 * Сдвинулась ли озвучка за время перетаскивания — положение или длина, в миллисекундах.
 *
 * Конец перетаскивания раньше сравнивал последнюю координату указателя (пиксели) с началом
 * реплики (миллисекунды): числа не совпадали никогда, и простое нажатие на ручку без движения
 * сохраняло реплику и запускало раздвижку соседей. Меньше полумиллисекунды — шум дробей, не
 * перемещение.
 */
export const voicingWasMoved = ({
    position,
    start,
    lengthPosition,
    length,
}: {
    position: number;
    start: number;
    lengthPosition: number;
    length: number;
}) => Math.abs(position - start) >= 0.5 || Math.abs(lengthPosition - length) >= 0.5;

/**
 * «Без автоправок»: как показать хвост озвучки, заходящий на следующую (хоть на 1 мс —
 * `manual_overlap_ms`, см. providers/AudioProvider/manualOverlaps). Такой наезд лежит в файлах
 * с загрузки — загрузка ничего не двигает и не сохраняет. Наложений не бывает и в этом режиме
 * (правило владельца 01.10.2026): следующая правка в папке отодвинет следующую озвучку позже —
 * на конец этой + 50 мс (так пишут в json раздвижка редактора и проход сервера), а размещение
 * сборки ставит её звук ровно на конец этой, без +50. Переводчик видит это на ленте заранее: низ
 * озвучки (лента вертикальная) закрашен красным на высоту наезда, а подсказка называет
 * миллисекунды и что будет. Без режима или без наезда — null, и дорожка выглядит как раньше.
 */
export const manualOverlapLook = ({
    manualOnly,
    overlapMs,
    lengthMs,
    zoomLevel,
    en,
}: {
    manualOnly: boolean;
    overlapMs: number | null | undefined;
    lengthMs: number;
    zoomLevel: number;
    en: boolean;
}): { shadow: string; hint: string } | null => {
    if (!manualOnly || !overlapMs || overlapMs <= 0) return null;
    const px = Math.round(Math.min(overlapMs, lengthMs) / zoomLevel);
    return {
        shadow: `inset 0 -${px}px 0 0 rgba(255, 0, 0, 0.75)`,
        hint: en
            ? `The tail of this voicing runs ${overlapMs} ms over the next one. Overlaps are removed even with no automatic edits: your next edit in this folder will push the next voicing later, to this one's end + 50 ms, and the build places its sound right at this one's end — nothing moves earlier, speed is not fitted. To place it yourself, move or shorten one of them.`
            : `Хвост этой озвучки заходит на следующую на ${overlapMs} мс. Наложения снимаются и без автоправок: следующая правка в этой папке отодвинет следующую озвучку позже — на конец этой + 50 мс, а сборка ставит её звук ровно на конец этой; раньше ничего не сдвигается, скорость не подгоняется. Чтобы поставить самим — отодвиньте или укоротите одну из них.`,
    };
};

/**
 * Почему озвучка не пошла за указателем (проба P50m20f, E7: перетаскивание вниз с зазором 50 мс до следующей реплики
 * дважды не сдвинуло блок — без единого слова, почему).
 *
 * Шаг перетаскивания принимается целиком или не принимается: блок не наезжает на соседнюю звучащую озвучку и не выходит
 * за папку, а пока сохраняется правка — не двигается вовсе. Здесь — какой упор сработал и сколько места до него было;
 * null — шаг принят. Блок целиком (ручка) длины не меняет: шаг больше его длины — не упор (раньше такой шаг тоже
 * отбрасывался молча — «длина стала бы ≤ 0», хотя длина у блока целиком та же).
 */
export type DragStopKind = "next" | "prev" | "folderEnd" | "folderStart" | "tooShort" | "saving";

export interface DragStop {
    kind: DragStopKind;
    /** Сколько места до упора было, мс (у «saving» и «tooShort» — 0). */
    roomMs: number;
}

export const dragStopOf = ({
    min,
    max,
    durationFull,
    position,
    lengthPosition,
    newPosition,
    newLengthPosition,
    wholeBlock,
    upperEdge,
}: {
    /** Границы шага — `getSelectedMaxMinLength` / `getMaxLength`. */
    min: number;
    max: number;
    durationFull: number;
    position: number;
    lengthPosition: number;
    newPosition: number;
    newLengthPosition: number;
    /** Тянут ручку блока (место целиком): упор у следующей — по концу блока. */
    wholeBlock: boolean;
    /** Тянут верхний край (начало вместе с длиной): длина не может стать ≤ 0. */
    upperEdge: boolean;
}): DragStop | null => {
    if (newPosition < min) {
        return { kind: min > 0 ? "prev" : "folderStart", roomMs: Math.max(0, position - min) };
    }
    const end = (wholeBlock ? lengthPosition : 0) + newPosition;
    if (end > max) {
        const room = max - ((wholeBlock ? lengthPosition : 0) + position);
        return { kind: max < durationFull ? "next" : "folderEnd", roomMs: Math.max(0, room) };
    }
    if (upperEdge && !(newLengthPosition > 0)) return { kind: "tooShort", roomMs: 0 };
    return null;
};

/**
 * Короткая подсказка у блока: почему не двигается и что сделать — простыми словами.
 *
 * Место до упора есть (`roomMs` > 0) — сдвинуть МОЖНО, на это место: отброшен только слишком большой шаг. Подсказка
 * говорит «так далеко не сдвинуть» и сколько можно, а не «позже не сдвинуть» (проверка круга AF: с зазором 50 мс блок
 * сдвигается на 50 мс). Вплотную (места нет) — «не сдвинуть» вовсе.
 */
export const dragStopHint = (stop: DragStop, en: boolean): string => {
    const ms = Math.round(stop.roomMs);
    const room = en ? `${ms} ms` : `${ms} мс`;
    const gap = ms > 0;
    switch (stop.kind) {
        case "next":
            if (gap) {
                return en
                    ? `Can't move it that far: the next voicing is ${room} away. Move it by ${room} at most, or move the next one later first.`
                    : `Так далеко не сдвинуть: до следующей озвучки ${room}. Сдвиньте не больше чем на ${room} или сначала сдвиньте следующую позже.`;
            }
            return en
                ? "Can't move it later: the next voicing starts right where this one ends. Move the next one later first, then this one."
                : "Позже не сдвинуть: следующая озвучка начинается вплотную к этой. Сначала сдвиньте позже следующую — потом эту.";
        case "prev":
            if (gap) {
                return en
                    ? `Can't move it that far: the previous voicing is ${room} away. Move it by ${room} at most, or move the previous one earlier first.`
                    : `Так далеко не сдвинуть: до предыдущей озвучки ${room}. Сдвиньте не больше чем на ${room} или сначала сдвиньте предыдущую раньше.`;
            }
            return en
                ? "Can't move it earlier: the previous voicing ends right where this one starts. Move the previous one earlier first, then this one."
                : "Раньше не сдвинуть: предыдущая озвучка кончается вплотную к этой. Сначала сдвиньте раньше предыдущую — потом эту.";
        case "folderEnd":
            if (gap) {
                return en
                    ? `Can't move it that far: the folder ends ${room} after this voicing. Move it by ${room} at most.`
                    : `Так далеко не сдвинуть: до конца папки ${room}. Сдвиньте не больше чем на ${room}.`;
            }
            return en ? "Can't move it later: this is the end of the folder." : "Позже не сдвинуть: здесь конец папки.";
        case "folderStart":
            if (gap) {
                return en
                    ? `Can't move it that far: the folder starts ${room} before this voicing. Move it by ${room} at most.`
                    : `Так далеко не сдвинуть: до начала папки ${room}. Сдвиньте не больше чем на ${room}.`;
            }
            return en
                ? "Can't move it earlier: this is the start of the folder."
                : "Раньше не сдвинуть: здесь начало папки.";
        case "tooShort":
            return en
                ? "Can't shorten it more: the start has reached the end of the voicing."
                : "Короче не сделать: начало озвучки дошло до её конца.";
        case "saving":
        default:
            return en
                ? "An edit is being saved — the voicing doesn't move until it's done. Wait a few seconds and drag again."
                : "Сохраняется правка — пока она не сохранится, озвучка не двигается. Подождите несколько секунд и потяните снова.";
    }
};
